import Image from 'next/image'
import {FC} from 'react'

import {getClassAssistContent} from '@/content/classAssist'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {PopularIndustriesSector} from '@/utils/class-assist/types'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'

import {Text, TextVariant} from '../../common/content/Text'

type ClassAssistPopularSectorsProps = {
  popularSectors: PopularIndustriesSector[]
  openInNewTab?: boolean
}

export const ClassAssistPopularSectors: FC<
  React.PropsWithChildren<ClassAssistPopularSectorsProps>
> = ({popularSectors, openInNewTab = false}) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {title, imageSrc, imageAlt} = getClassAssistContent(language).popularSectors
  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title)}
      </Text>
      <div className="wrapper">
        <div className="image-wrapper">
          <Image
            src={imageSrc}
            alt={imageAlt}
            style={{objectFit: 'contain'}}
            width={150}
            height={185}
          />
        </div>
        <ul className="list">
          {popularSectors.map(({industryName, industrySlug, sectorName, sectorSlug}) => (
            <li key={sectorSlug} className="item">
              <Text>
                <Link
                  href={routeTo.classAssist.industry(industrySlug, sectorSlug)}
                  prefetch={false}
                  legacyBehavior
                >
                  <a
                    className="underline"
                    target={openInNewTab ? '_blank' : '_self'}
                    rel={openInNewTab ? 'noopener noreferrer' : ''}
                  >
                    {industryName} - {sectorName}
                  </a>
                </Link>
              </Text>
            </li>
          ))}
        </ul>
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: ${theme.spacing[8]} auto 0;
        }

        .list {
          column-count: 2;
          margin-bottom: -${theme.spacing[3]};
        }

        .item {
          position: relative;
          margin: 0 ${theme.spacing[5]} ${theme.spacing[3]};
          display: table;
          padding-left: ${theme.spacing[3]};
        }

        .item::before {
          content: '';
          width: 5px;
          height: 5px;
          position: absolute;
          left: 0;
          top: 10px;
          background-color: ${theme.palette.text.default};
          border-radius: 50%;
        }

        .image-wrapper {
          position: relative;
          width: 150px;
          height: 185px;
          flex: 0 0 150px;
          margin-right: ${theme.spacing[8]};
          margin-left: ${theme.spacing[5]};
        }

        @media ${TABLET_SCREENSIZE} {
          .image-wrapper {
            margin-right: ${theme.spacing[6]};
            margin-left: ${theme.spacing[4]};
          }
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .list {
            column-count: inherit;
          }

          .image-wrapper {
            margin-right: ${theme.spacing[7]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            display: block;
            margin-top: ${theme.spacing[6]};
          }

          .image-wrapper {
            display: none;
          }

          .item {
            margin: 0 0 ${theme.spacing[3]} ${theme.spacing[1]};
          }
        }
      `}</style>
    </>
  )
}
